import { useMemo } from 'react';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';

import { usePrimaryDecisionMakerConnection } from '../../../decisionMakers/hooks/usePrimaryDecisionMakerConnection';
import { useMediaHitsContext } from '../../../MediaHitsContainer/shared/context';
import { useMediaHitsPermissions } from '../../../MediaHitsContainer/shared/hooks/useMediaHitsPermissions';

type Result = ModelHookResult<{
	sectionIsShown: boolean;
	decisionMakers: {
		isShown: boolean;
		count: number;
		first?: { displayName: string };
	};
	mediaHits: {
		isShown: boolean;
		count: number;
	};
	updates: {
		isShown: boolean;
		count: number;
	};
}>;

export const useContentSummary = (): Result => {
	const { data: decisionMakersConnection } = usePrimaryDecisionMakerConnection();

	const {
		data: { mediaEmbeds },
	} = useMediaHitsContext();

	const {
		data: { canAdd },
	} = useMediaHitsPermissions();

	const {
		data: {
			petition: { petitionUpdatesConnection },
		},
	} = usePetitionDetailsPageData();

	// Filter mediaEmbeds for media hits rendered properly
	const validMediaEmbeds = useMemo(() => {
		return mediaEmbeds?.filter((mediaRendered) => mediaRendered.valid) || [];
	}, [mediaEmbeds]);

	const decisionMakers = useMemo(() => {
		return {
			isShown: decisionMakersConnection.totalCount > 0,
			count: decisionMakersConnection.totalCount,
			first: decisionMakersConnection.decisionMakers[0],
		};
	}, [decisionMakersConnection]);

	const mediaHits = useMemo(() => {
		const mediaEmbedsCount = (canAdd ? mediaEmbeds : validMediaEmbeds)?.length ?? 0;

		return {
			isShown: mediaEmbedsCount > 0,
			count: mediaEmbedsCount ?? 0,
		};
	}, [canAdd, mediaEmbeds, validMediaEmbeds]);

	const updates = useMemo(() => {
		const { totalCount } = petitionUpdatesConnection;

		return {
			isShown: totalCount > 0,
			count: totalCount,
		};
	}, [petitionUpdatesConnection]);

	return useMemo(() => {
		return {
			data: {
				sectionIsShown: decisionMakers.isShown || mediaHits.isShown || updates.isShown,
				decisionMakers,
				mediaHits,
				updates,
			},
			actions: {},
		};
	}, [decisionMakers, mediaHits, updates]);
};
